import { IUnitActivityData } from "../features/dashboard/eventDataTypes";
import { IFailedRequest } from "./login.services";
import { request } from "./utils";
import { IResidentContact } from "./common.services";
import { IPhoneNumber, IResidentPhoneNumber } from "./header.services";

export const dashboardServices = {
  getUnits,
  getCommunityUnits,
  getCommunityEvents,
  getActiveEvents,
  getResolvedEvents,
  getEventsSince,
  getUnitEvents,
  markAllWarningsRead,
  assignEventResponder,
  resolveEvent,
  postNewComment,
  getUnitDashboard,
  getUnitActivityData,
  togglePauseNotifications,
  getEvent,
  toggleWatchedFlag,
};

export interface IOrgRole {
  community_id: string | null;
  all_units: boolean | null;
  email: string | null;
  name: string | null;
  user_id: string | null;
  time_created: string | null;
}

export interface IResident {
  first_name: string | null;
  id: string;
  last_name: string | null;
  city?: string | null;
  postal_code?: string | null;
  state?: string | null;
  street?: string | null;
  street2?: string | null;
  contacts?: IResidentContact[] | null;
  unit_id?: string | null;
  phone_numbers?: IResidentPhoneNumber[] | IPhoneNumber[] | null;
  date_of_birth?: string | null;
}

export interface IUnit {
  community_id: string | null;
  custom_name: string | null;
  id: string;
  is_notifications_paused: boolean;
  is_occupied: boolean | null;
  is_watched: boolean;
  last_motion_time: string | null;
  name: string;
  residents: IResident[] | null;
  site_id: string | null;
  unit_number: number;
  unit_type: string | null;
  time_updated: string;
  time_created?: string | null;
  gateway_id?: string | null;
  security_notification_enabled: boolean | null;
  pause_notifications_expiration: string | null;
  time_zone: string;
  tile_counts?: ITileCounts;
}

// A unit's badge counts for the current user, sent when the change feed is on
export interface ITileCounts {
  unread: number;
  unresolved: number;
  newest_unread: { event_id: string; event_type: string } | null;
  version: string;
}

export type ChangeKind =
  | "unread_added"
  | "unread_removed"
  | "unresolved_added"
  | "unresolved_updated"
  | "unresolved_removed"
  | "counts_changed";

// One change for the current user, read from users/{uid}/changes
export interface IChangeRecord {
  community_id: string;
  unit_id: string;
  kind: ChangeKind;
  event_id?: string;
  event_key?: string;
  event?: Record<string, any>;
  unit_counts: ITileCounts;
}

// A community, a list of unit ids that can span communities, or every community of the user
export type EventScope =
  | { community_id: string }
  | { unit_id: string }
  | { all_communities: 1 };

// Turned on once the backend scopes all communities events to the user itself
export const eventsAllCommunities =
  process.env.REACT_APP_EVENTS_ALL_COMMUNITIES === "true";

export interface IEventsAsOf {
  events: IEvent[];
  asOf: string | null;
}

export interface IZone {
  id: string;
  unit_id: string;
  name: string | null;
  zone_type: string | null;
}

export interface IReportStatus {
  id: string;
  community_id: string;
  unit_id: string;
  report_date: string;
  report_type: string;
  status: string;
  time_created: string;
  time_queued?: string | null;
  time_started?: string | null;
  time_completed?: string | null;
  time_last_updated?: string | null;
  version?: string | null;
  recommendation?: string | null;
}

export interface IReport {
  id: string;
  community_id: string;
  unit_id: string;
  report_date: string;
  report_type: string;
  report: string;
  time_completed: string;
}

export interface IEventNotes {
  description: string | null;
  event_id: string;
  id: string;
  name: string | null;
  timestamp: string | null;
  type: string | null;
  user_id: string | null;
}

export interface IEventNotesData {
  description?: string;
  event_id: string;
}
export interface IWatchedFlagData {
  unitID: string;
  isWatched: boolean;
}

export interface IEvent {
  // Firebase retrieved
  firebaseKey?: string;
  event_type: string;
  id: string;
  unit_id: string;
  responder_id: string | null;
  zone_id: string | null;
  status: string;
  severity: string;
  time_created: string;
  data: string;
  notification_type: string | null;
  // API retrieved
  community_id?: string | null;
  email?: string;
  event_notes?: IEventNotes[];
  event_resolution_id?: string | null;
  event_status?: null;
  first_name: string;
  last_name?: string | null;
  level?: number | null;
  read_status: string | null;
  time_assigned?: string | null;
  time_resolved?: string | null;
  system_issues?: boolean | null;
  is_delayed?: boolean | null;
  tag_id?: string | null;
  last_scanned?: string | null;
}

export interface IRFIDTag {
  id: string;
  community_id: string;
  unit_id: string;
  serial_number: string;
  time_created?: string;
  last_scanned?: string;
}

export interface IAssignEventData {
  eventID: string;
  responderID: string;
}

export interface IResolveEventData {
  eventID: string;
  resolutionID: string;
  eventNote?: string;
}
export interface ITogglePauseNotificationData {
  unitId: string;
  eventId: string;
  enabled: boolean;
}
export interface IToggleResponseData {
  unit: IUnit;
  event: IEvent;
}
export interface IUnitDashboard {
  motion_data: {
    zone_name: string | null;
    last_motion_time: string | null;
  } | null;
  bathroom_data: {
    category: string | null;
    visits: number;
  } | null;
  heat_index_data: {
    category: string | null;
    celsius: number;
  } | null;
  sleep_data: {
    duration: number;
  } | null;
  help_data: {
    events: string[];
    help_button_press_time: string;
  } | null;
  kitchen_data: {
    last_motion_event_time: string | null;
  } | null;
  local_time_updated: string | null;
}

export interface SystemStatus {
  planned_maintenance: {
    end_time: string;
    start_time: string;
    maintenance_type: string;
  };
  status: string;
}
/**
 * Loads the units given a community id
 * @param {string} community_id Community id to retrieve units *
 * @returns {IUnit[]} An array containing the communities linked to the user
 */

async function getUnits(community_id: string): Promise<IUnit[]> {
  const requestOptions: RequestInit = {
    body: null,
  };

  const data = await request(
    `/units?community_id=${community_id}`,
    requestOptions
  );

  if (data instanceof Error) {
    const error: IFailedRequest = JSON.parse(data.message);
    return Promise.reject(error);
  } else {
    return Promise.resolve(data as IUnit[]);
  }
}

/**
 * Loads the units for a user's communities
 * @returns {IUnit[]} An array containing the communities linked to the user
 */

async function getCommunityUnits(): Promise<IUnit[]> {
  const requestOptions: RequestInit = {
    body: null,
  };

  const data = await request(`/units?all_communities=1`, requestOptions);

  if (data instanceof Error) {
    const error: IFailedRequest = JSON.parse(data.message);
    return Promise.reject(error);
  } else {
    return Promise.resolve(data as IUnit[]);
  }
}

/**
 * Loads the events for the community units
 * @param {string} community_id Community id to retrieve units *
 * @returns {IUnit[]} An array containing the communities linked to the user
 */

async function getCommunityEvents(community_id: string): Promise<IEvent[]> {
  const requestOptions: RequestInit = {
    body: null,
  };

  const data = await request(
    `/events?community_id=${community_id}&status=unassigned,assigned&order=desc`,
    requestOptions
  );

  if (data instanceof Error) {
    const error: IFailedRequest = JSON.parse(data.message);
    return Promise.reject(error);
  } else {
    return Promise.resolve(data as IEvent[]);
  }
}

export const WARNING_EVENTS_LIMIT = 100;

// The code of a list request that got no answer, like one sent before the network is back
export const NO_RESPONSE = "NO_RESPONSE";
// The events of a list response, undefined for a request that got no answer.
// An empty response comes back as true
export const responseEvents = (response: any): IEvent[] | undefined =>
  response?.data === true
    ? []
    : Array.isArray(response?.data)
    ? response.data
    : undefined;

// The code of a list request the server refused, like events across communities a user can't all see
export const ACCESS_REFUSED = "ACCESS_REFUSED";

// The API returns 50 events when no limit is given, so lists ask for pages this size
const EVENTS_PAGE_LIMIT = 500;

type EventParams = Record<string, string | number>;

// One page of events with the database time it was read
async function getEventsPage(
  scope: EventScope,
  params: EventParams
): Promise<IEventsAsOf> {
  const query = { ...scope, ...params };

  // A long unit list can outgrow a URL, so it goes in the body
  const response =
    "unit_id" in scope
      ? await request(`/events/search`, {
          method: "POST",
          body: JSON.stringify(query),
          withAsOf: true,
        })
      : await request(
          `/events?${Object.entries(query)
            .map(([key, value]) => `${key}=${value}`)
            .join("&")}`,
          { body: null, withAsOf: true }
        );

  if (response instanceof Error) {
    const error: IFailedRequest = JSON.parse(response.message);
    if (error.status === 401 || error.status === 403)
      error.code = ACCESS_REFUSED;
    return Promise.reject(error);
  }
  const events = responseEvents(response);
  if (events === undefined) {
    const error: IFailedRequest = { code: NO_RESPONSE, message: "" };
    return Promise.reject(error);
  }
  return Promise.resolve({ events, asOf: response.asOf });
}

// Reads every page unless a limit is given. A search across communities is slow,
// so its pages are read two at a time. The list's as-of is the earliest page's
async function getEventsAsOf(
  scope: EventScope,
  params: EventParams,
  limit?: number
): Promise<IEventsAsOf> {
  if (limit !== undefined) return getEventsPage(scope, { ...params, limit });

  const pagesAtOnce = "community_id" in scope ? 1 : 2;
  const pages: IEventsAsOf[] = [];
  let lastPageFull = true;
  while (lastPageFull) {
    const batch = await Promise.all(
      Array.from({ length: pagesAtOnce }, (_, index) =>
        getEventsPage(scope, {
          ...params,
          limit: EVENTS_PAGE_LIMIT,
          page: pages.length + index,
        })
      )
    );
    pages.push(...batch);
    lastPageFull = batch.every(
      (page) => page.events.length === EVENTS_PAGE_LIMIT
    );
  }

  // Paging while events change can repeat one across pages
  const events = new Map(
    pages.flatMap((page) => page.events).map((event) => [event.id, event])
  );
  const asOfs = pages.map((page) => page.asOf);
  return {
    events: Array.from(events.values()),
    asOf: asOfs.includes(null) ? null : (asOfs as string[]).sort()[0],
  };
}

/**
 * Loads the open critical events or the newest unread warnings, with the database time they were read
 * @param {EventScope} scope Community id, or unit ids that can span communities
 * @param {boolean} critical Loads critical events when true, unread warnings otherwise
 * @returns {IEventsAsOf} The events and the X-As-Of header, null when the change feed is off
 */
async function getActiveEvents(
  scope: EventScope,
  critical: boolean
): Promise<IEventsAsOf> {
  return critical
    ? getEventsAsOf(scope, { status: "unassigned,assigned", order: "desc" })
    : getEventsAsOf(
        scope,
        { read_status: "unread", notification_type: "soft", order: "desc" },
        WARNING_EVENTS_LIMIT
      );
}

/**
 * Loads the events resolved since a time
 * @param {EventScope} scope Community id, or unit ids that can span communities
 * @param {string} since UTC time the events were created after
 * @returns {IEventsAsOf} The events and the X-As-Of header
 */
async function getResolvedEvents(
  scope: EventScope,
  since: string
): Promise<IEventsAsOf> {
  return getEventsAsOf(scope, {
    status: "resolved",
    start_time: since,
    order: "desc",
  });
}

/**
 * Loads every event created since a time
 * @param {EventScope} scope Community id, or unit ids that can span communities
 * @param {string} since UTC time the events were created after
 * @returns {IEvent[]} The events, newest first
 */
async function getEventsSince(
  scope: EventScope,
  since: string
): Promise<IEvent[]> {
  const { events } = await getEventsAsOf(scope, {
    start_time: since,
    order: "desc",
  });
  return events;
}

/**
 * Events for a single unit
 */
async function getUnitEvents(unitId: string): Promise<IEvent[]> {
  const requestOptions: RequestInit = {
    body: null,
  };

  const data = await request(
    `/events?unit_id=${unitId}&status=unassigned,assigned,resolved&order=desc`,
    requestOptions
  );

  if (data instanceof Error) {
    const error: IFailedRequest = JSON.parse(data.message);
    return Promise.reject(error);
  } else {
    return Promise.resolve(data as IEvent[]);
  }
}

/**
 * Get a specific event
 * @param {string} event_id event id
 * @returns {IEvent} The event
 */

async function getEvent(
  event_id: string,
  markAsRead: boolean = true
): Promise<IEvent> {
  const requestOptions: RequestInit = {
    body: null,
  };

  const data = await request(
    `/events/${event_id}${markAsRead ? "?mark_as_read=1" : ""}`,
    requestOptions
  );

  if (data instanceof Error) {
    const error: IFailedRequest = JSON.parse(data.message);
    return Promise.reject(error);
  } else {
    return Promise.resolve(data as IEvent);
  }
}

/**
 * Marks all warnings as read
 * @param {string} community_id Community ID
 * @returns {boolean} Indicates the succesful completion of the API call
 */

async function markAllWarningsRead(community_id: string): Promise<boolean> {
  const requestOptions: RequestInit = {
    method: "PUT",
    body: JSON.stringify({
      notification_type: "soft",
    }),
  };

  const data = await request(
    `/event_status/mark_unread?community_id=${community_id}`,
    requestOptions
  );

  if (data instanceof Error) {
    const error: IFailedRequest = JSON.parse(data.message);
    return Promise.reject(error);
  } else {
    return Promise.resolve(true);
  }
}

/**
 * Assigns an event to a responder
 * @param {string} community_id Community ID
 * @returns {IEvent} The updated event
 */

async function assignEventResponder(
  assignData: IAssignEventData
): Promise<IEvent> {
  const requestOptions: RequestInit = {
    method: "PUT",
    body: JSON.stringify({
      responder_id: assignData.responderID,
    }),
  };

  const data = await request(
    `/events/${assignData.eventID}/assign`,
    requestOptions
  );

  if (data instanceof Error) {
    const error: IFailedRequest = JSON.parse(data.message);
    return Promise.reject(error);
  } else {
    return Promise.resolve(data as IEvent);
  }
}

/**
 * Add a new comment on an event
 * @param {string} event_id event id
 * @param {string} description the content of the comment
 * @returns {IEventNotes} The updated event with the new comment
 */

async function postNewComment(
  eventNotesData: IEventNotesData
): Promise<IEventNotes> {
  const requestOptions: RequestInit = {
    method: "POST",
    body: JSON.stringify({
      event_id: eventNotesData.event_id,
      description: eventNotesData.description,
    }),
  };

  const data = await request(`/event_notes`, requestOptions);

  if (data instanceof Error) {
    const error: IFailedRequest = JSON.parse(data.message);
    return Promise.reject(error);
  } else {
    return Promise.resolve(data);
  }
}

/**
 * Resolve an event with a resolution id
 * @param {IResolveEventData} resolveData Event id, resolution id and optional note
 * @returns {IEvent} The updated event
 */

async function resolveEvent(resolveData: IResolveEventData): Promise<IEvent> {
  const requestOptions: RequestInit = {
    method: "PUT",
    body: JSON.stringify({
      event_resolution_id: resolveData.resolutionID,
      event_note: resolveData.eventNote,
    }),
  };

  const data = await request(
    `/events/${resolveData.eventID}/resolve`,
    requestOptions
  );

  if (data instanceof Error) {
    const error: IFailedRequest = JSON.parse(data.message);
    return Promise.reject(error);
  } else {
    return Promise.resolve(data as IEvent);
  }
}

/**
 * Resume or "un-pause" notifications for a unit
 * @param {ITogglePauseNotificationData} toggleData unit id and wether notifications should be enabled
 * @returns {IUnit} The updated event
 */

async function togglePauseNotifications(
  toggleData: ITogglePauseNotificationData
): Promise<IUnit> {
  const ans: { [key: string]: any } = {};
  ans["is_notifications_paused"] = toggleData.enabled;
  if (!toggleData.enabled) {
    ans["pause_notifications_expiration"] = null;
  }
  const requestOptions: RequestInit = {
    method: "PUT",
    body: JSON.stringify(ans),
  };

  const data = await request(`/units/${toggleData.unitId}`, requestOptions);

  if (data instanceof Error) {
    const error: IFailedRequest = JSON.parse(data.message);
    return Promise.reject(error);
  } else {
    return Promise.resolve(data as IUnit);
  }
}

/**
 * Load the unit status or "dashboard"
 * @param {string} unit_id unit id
 * @returns {IUnitDashboard} The status
 */

async function getUnitDashboard(unit_id: string): Promise<IUnitDashboard> {
  const requestOptions: RequestInit = {
    body: null,
  };

  const data = await request(`/units/${unit_id}/dashboard`, requestOptions);

  if (data instanceof Error) {
    const error: IFailedRequest = JSON.parse(data.message);
    return Promise.reject(error);
  } else {
    return Promise.resolve(data as IUnitDashboard);
  }
}
/**
 * Load the unit activity data
 * @param {string} unit_id unit id
 * @returns {IUnitActivityData} The status
 */

async function getUnitActivityData(
  unit_id: string,
  startTime: string,
  endTime: string
): Promise<IUnitActivityData> {
  const requestOptions: RequestInit = {
    body: null,
  };

  const data = await request(
    `/units/${unit_id}/activity_data?start_time=${startTime}&end_time=${endTime}&version=2&contact_count=1`,
    requestOptions
  );

  if (data instanceof Error) {
    const error: IFailedRequest = JSON.parse(data.message);
    return Promise.reject(error);
  } else {
    return Promise.resolve(data as IUnitActivityData);
  }
}

/**
 * Toggles the watched parameter for units
 * @param {string} unitID unit id
 * @param {is_watched} isWatched indicated if unit is being watched or not
 * @returns {IUnit} The status
 */

async function toggleWatchedFlag(
  watchedFlagData: IWatchedFlagData
): Promise<IUnit> {
  const requestOptions: RequestInit = {
    method: "PUT",
    body: JSON.stringify({
      is_watched: watchedFlagData.isWatched,
    }),
  };

  const data = await request(
    `/units/${watchedFlagData.unitID}`,
    requestOptions
  );

  if (data instanceof Error) {
    const error: IFailedRequest = JSON.parse(data.message);
    return Promise.reject(error);
  } else {
    return Promise.resolve(data as IUnit);
  }
}
